<script setup>
import { computed, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversation: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { uiSettings } = useUISettings();

const isLoading = ref(false);
const currentChat = computed(() => getters.getSelectedChat.value || {});
const isContactPanelOpen = computed(
  () =>
    uiSettings.value.is_contact_sidebar_open && Boolean(currentChat.value.id)
);
const modalTitle = computed(() =>
  t('CRM.KANBAN.CONVERSATION_MODAL_TITLE', {
    id: props.conversation?.id,
  })
);

const loadConversation = async conversation => {
  if (!conversation?.id) return;

  isLoading.value = true;
  try {
    await store.dispatch('getConversation', conversation.id);

    if (!props.show || props.conversation?.id !== conversation.id) return;

    const storedConversation =
      getters.getConversationById.value(conversation.id) || conversation;

    await store.dispatch('setActiveInbox', storedConversation.inbox_id);
    await store.dispatch('setActiveChat', { data: storedConversation });
  } catch (error) {
    emit('close');
    useAlert(t('CRM.KANBAN.CONVERSATION_LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => [props.show, props.conversation?.id],
  ([show]) => {
    if (show) loadConversation(props.conversation);
  }
);

useEventListener(document, 'keydown', event => {
  if (props.show && event.code === 'Escape') {
    emit('close');
    event.stopPropagation();
  }
});
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="show"
        class="fixed inset-0 z-[9990] flex items-center justify-center p-6 bg-n-alpha-black2 backdrop-blur-[4px]"
        @click.self="emit('close')"
      >
        <section
          role="dialog"
          aria-modal="true"
          :aria-label="modalTitle"
          class="flex flex-col w-full h-full max-w-[96rem] overflow-hidden border rounded-xl shadow-xl bg-n-surface-1 border-n-weak"
        >
          <header
            class="flex items-center justify-between flex-none h-12 gap-4 px-4 border-b border-n-weak bg-n-surface-2"
          >
            <h2 class="font-medium truncate text-n-slate-12">
              {{ modalTitle }}
            </h2>
            <Button
              ghost
              slate
              sm
              icon="i-lucide-x"
              :aria-label="$t('CRM.KANBAN.CLOSE_CONVERSATION')"
              @click="emit('close')"
            />
          </header>

          <div
            v-if="isLoading"
            class="flex items-center justify-center flex-1 gap-2 text-n-slate-11"
          >
            <Spinner class="size-5" />
            {{ $t('CRM.KANBAN.CONVERSATION_LOADING') }}
          </div>

          <div v-else class="flex flex-1 min-w-0 min-h-0">
            <ConversationBox
              class="flex-1 [&.conversation-details-wrap]:!border-0"
              :inbox-id="currentChat.inbox_id"
              :is-on-expanded-layout="false"
            >
              <SidepanelSwitch v-if="currentChat.id" />
            </ConversationBox>
            <ConversationSidebar
              v-if="isContactPanelOpen"
              :current-chat="currentChat"
            />
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
